import { useMemo } from "react";
import { useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import type { GridFilterModel, GridSortModel } from "@mui/x-data-grid-premium";
import { msalInstance } from "../authconfig";

export type GridView = {
  filterModel: GridFilterModel;
  sortModel: GridSortModel;
};

export type OverviewGrid = "counterparties" | "brokers" | "countries";

const STORAGE_PREFIX = "ma-hub-overview";

// Urgent first is the long-standing default on both overviews.
const DEFAULT_VIEW: GridView = {
  filterModel: { items: [] },
  sortModel: [{ field: "urgent", sort: "desc" }]
};

/**
 * Storage keys are scoped to the signed-in user so a shared machine does not leak one
 * person's saved view to the next. Read straight from MSAL rather than through a hook: the
 * app gates rendering on authentication, so an account always exists by the time a grid
 * mounts, and the key has to be resolved before the atom is created.
 */
const storageKey = (grid: OverviewGrid) => {
  const account =
    msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  const oid = (account?.idTokenClaims as { oid?: string } | undefined)?.oid;
  return `${STORAGE_PREFIX}-${oid ?? account?.localAccountId ?? "anonymous"}-${grid}`;
};

// atomWithStorage needs its key up front, and the key depends on who is signed in, so the
// atoms are created lazily and cached rather than declared at module scope.
const atomCache = new Map<
  string,
  ReturnType<typeof atomWithStorage<GridView>>
>();

const gridViewAtom = (grid: OverviewGrid) => {
  const key = storageKey(grid);
  const existing = atomCache.get(key);
  if (existing) return existing;

  const created = atomWithStorage<GridView>(key, DEFAULT_VIEW);
  atomCache.set(key, created);
  return created;
};

/**
 * Filter and sort selection for one of the two active overview grids, persisted per user.
 * Navigating into a counterparty unmounts the grid, so without this the selection is lost on
 * every round trip.
 */
export const useGridView = (grid: OverviewGrid) => {
  const atom = useMemo(() => gridViewAtom(grid), [grid]);
  return useAtom(atom);
};

/**
 * Drops persisted filter items that reference a column the current user does not have.
 * marketAccessHandler is only present for global lifecycle roles, and a stale filter on a
 * missing field would silently hide every row.
 */
export const pruneFilterModel = (
  filterModel: GridFilterModel,
  availableFields: Set<string>
): GridFilterModel => {
  const items = (filterModel.items ?? []).filter((item) =>
    availableFields.has(item.field)
  );
  return items.length === (filterModel.items ?? []).length
    ? filterModel
    : { ...filterModel, items };
};
